import { get } from 'lodash';
import { useTranslation } from '@lib/useTypedTranslation';
import styled from 'styled-components';
import { CheckBox } from '../../../../components/controls/checkbox';
import { CustomValidatedInput } from '../../../../components/customValidatedInput/customValidatedInput';
import { WorldSettings } from '../../../../services/config/config';
import { useFeatureTogglesContext } from '../../../../context/featureToggles';
import { InfoPopover } from '@components/tooltip/infoPopover';

const Block = styled.div`
  border: 1px solid #d7e2eb;
  margin-bottom: 1.5rem;
`;

const Title = styled.div`
  background: #f3f7fb;
  border-bottom: 1px solid #d7e2eb;
  padding: 0.3rem;
  margin-bottom: 1rem;
  font-size: 1rem;
  font-weight: 600;
`;

const Body = styled.div`
  padding-left: 1.15rem;
  font-size: 0.875rem;
  font-weight: 400;
  padding-bottom: 1.4rem;
  text-align: left;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  width: 40%;
  padding: 0.2rem;
`;

const PRow = styled.div`
  width: 40%;
  margin: 0.75rem 0;
  padding: 0.2rem;
  input {
    margin-left: 0.5rem;
    margin-right: 0.5rem;
  }
`;

export interface IProps {
  worldSettings: WorldSettings,
  onDataChanged: (data: WorldSettings) => void
}

export function DevicesSettings(props: IProps) {
  const { t } = useTranslation('settings');
  const { worldSettings } = props;
  const featureToggles = useFeatureTogglesContext();
  const systemText = {
    android: t('LOCATION_ANDROID_ENABLED'),
    windows: t('LOCATION_WINDOWS_ENABLED')
  };

  function onDataChanged(data: boolean | number, key: any): void {
    props.onDataChanged({ ...worldSettings, [key]: data });
  }

  const mobileSignalReportingText = {
    android: t('MOBILE_SIGNAL_REPORTING_ANDROID'),
    windows: t('MOBILE_SIGNAL_REPORTING_WINDOWS')
  };

  const wifiSignalReportingText = {
    android: t('WIFI_SIGNAL_REPORTING_ANDROID')
  };

  return (
    <>
      <Block className="devices-configuration">
        <Title className="devices-configuration_header">{t('LOCATION_INFORMATION_TITLE')}</Title>
        <Body className="devices-configuration_body">
          {Object.keys(systemText).map(system => {
            const name = get(systemText, `${system}`);
            const id = `${(name.toLowerCase()).replace(/ /g, "_")}`;
            return (
              <Row key={id}>{name}
                <CheckBox
                  key={id}
                  name={id}
                  className="fancy-checkbox"
                  checked={get(worldSettings, `${system}LocationCollectionEnabled`)}
                  onChange={(event) => onDataChanged(event.checked, `${system}LocationCollectionEnabled`)} />
              </Row>
            );
          })}
        </Body>
      </Block>
      <Block className="devices-configuration">
        <Title className="devices-configuration_header">{t('MOBILE_SIGNAL_REPORTING_TITLE')}</Title>
        <Body className="devices-configuration_body">
          {Object.keys(mobileSignalReportingText).map(deviceTypeText => {
            const name = get(mobileSignalReportingText, `${deviceTypeText}`);
            const id = `${(name.toLowerCase()).replace(/ /g, "_")}MobileSignalReporting`;
            return (
              <Row key={id}>{name}
                <CheckBox
                  key={id}
                  name={id}
                  className="fancy-checkbox"
                  checked={get(worldSettings, `${deviceTypeText}MobileSignalLevelCollectionEnabled`)}
                  onChange={(event) => onDataChanged(event.checked, `${deviceTypeText}MobileSignalLevelCollectionEnabled`)} />
              </Row>
            );
          })}
        </Body>
      </Block>
      {featureToggles.showAccessPointsPage && <Block className="devices-configuration">
        <Title className="devices-configuration_header">{t('WIFI_SIGNAL_REPORTING_TITLE')}</Title>
        <Body className="devices-configuration_body">
          {Object.keys(wifiSignalReportingText).map(deviceTypeText => {
            const name = get(wifiSignalReportingText, `${deviceTypeText}`);
            const id = `${(name.toLowerCase()).replace(/ /g, "_")}WifiSignalReporting`;
            return (
              <Row key={id}>{name}
                <CheckBox
                  key={id}
                  name={id}
                  className="fancy-checkbox"
                  checked={get(worldSettings, `${deviceTypeText}WifiSignalLevelCollectionEnabled`)}
                  onChange={(event) => onDataChanged(event.checked, `${deviceTypeText}WifiSignalLevelCollectionEnabled`)} />
              </Row>
            );
          })}
        </Body>
      </Block>}
      <Block className="devices-configuration">
        <Title className="devices-configuration_header">{t('DROPS_ENABLED_TITLE')}</Title>
        <Body className="devices-configuration_body">
          <Row>{t('DROPS_ANDROID_ENABLED')}
            <CheckBox
              name="drops_enabled"
              className="fancy-checkbox"
              checked={worldSettings.androidDropDetectionEnabled}
              onChange={(event) => onDataChanged(event.checked, 'androidDropDetectionEnabled')} />
          </Row>
        </Body>
      </Block>
      {featureToggles.showDeviceFinderSettings && <Block className="devices-configuration">
        <Title className="devices-configuration_header">{t('DEVICE_FINDER_TITLE')}</Title>
        <Body className="devices-configuration_body">
          <Row>{t('DEVICE_FINDER_ENABLED')}
            <CheckBox
              name="device_finder_enabled"
              className="fancy-checkbox"
              checked={worldSettings.androidDeviceBeaconingEnabled}
              onChange={(event) => onDataChanged(event.checked, 'androidDeviceBeaconingEnabled')} />
          </Row>
          <PRow>{t('DEVICE_FINDER_IDLE_TIME')}
            &nbsp;<InfoPopover labelText={'about beacon activation timer'}>
              {t('DEVICE_FINDER_IDLE_TIME_TOOLTIP')}
            </InfoPopover>
            &nbsp;<CustomValidatedInput
              value={worldSettings.androidDeviceBeaconingActivationDelay}
              disabled={!worldSettings.androidDeviceBeaconingEnabled}
              validationRule={(value: number) => value >= 5 && value <= 120}
              errorText={t('DEVICE_FINDER_IDLE_TIME_ERROR', { min: 5, max: 120 })}
              onChange={(value: number) => onDataChanged(value, 'androidDeviceBeaconingActivationDelay')}
            />
            &nbsp;{t('DEVICE_FINDER_IDLE_TIME_UNIT', { count: worldSettings.androidDeviceBeaconingActivationDelay })}
          </PRow>
          <Row>{t('DEVICE_FINDER_SKIP_ON_CHARGE')}
            <CheckBox
              name="device_finder_skip_on_charge"
              className="fancy-checkbox"
              checked={worldSettings.androidDeviceBeaconingDisableIfOnPower}
              disabled={!worldSettings.androidDeviceBeaconingEnabled}
              onChange={(event) => onDataChanged(event.checked, 'androidDeviceBeaconingDisableIfOnPower')} />
          </Row>
          <Row>{t('DEVICE_FINDER_SKIP_ON_SCREEN_ON')}
            <CheckBox
              name="device_finder_skip_on_screen_on"
              className="fancy-checkbox"
              checked={worldSettings.androidDeviceBeaconingDisableIfScreenOn}
              disabled={!worldSettings.androidDeviceBeaconingEnabled}
              onChange={(event) => onDataChanged(event.checked, 'androidDeviceBeaconingDisableIfScreenOn')} />
          </Row>
        </Body>
      </Block>}
    </>
  );
}
